import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Brain, Award, Play, Pause, Volume2, Zap, Layers, Book, Sparkles, Flag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import SummaryPanel from '../components/SummaryPanel';
import QuizModal from '../components/QuizModal';
import SpeedControl from '../components/SpeedControl';
import ReadingSpeedDial from '../components/ReadingSpeedDial';
import SpeedDial from '../components/SpeedDial';
import ChapterView from '../components/ChapterView';
import ChapterPickerInline from '../components/ChapterPickerInline';
import { usePlaybackPreferences } from '../hooks/usePlaybackPreferences';
import DonateButton from '../components/DonateButton';
import ItemComments from '../components/ItemComments';
import ReportContentModal from '../components/ReportContentModal';
import toast from 'react-hot-toast';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { authFetch } from '../utils/authUtils';
import VoicePreviewButton from '../components/VoicePreviewButton';
import BookCover from '../components/BookCover';

// Preset speeds — shared between controls so users get predictable stepping.
const FLASH_PRESETS = [
  { label: 'Slow', value: 500 },    // 120 WPM
  { label: 'Normal', value: 300 },  // 200 WPM
  { label: 'Fast', value: 200 },    // 300 WPM
  { label: 'Turbo', value: 120 },   // 500 WPM
];
const CHUNK_PRESETS = [
  { label: 'Slow', value: 3500 },
  { label: 'Normal', value: 2000 },
  { label: 'Fast', value: 1500 },
  { label: 'Turbo', value: 1000 },
];
const TTS_PRESETS = [
  { label: 'Slow', value: 0.75 },
  { label: 'Normal', value: 1.0 },
  { label: 'Fast', value: 1.5 },
  { label: 'Turbo', value: 2.0 },
  { label: 'Hyper', value: 3.0 },
];

// SpeedDial step ladders — ascending visually means clockwise on the dial.
// Flash/Chunk use descending ms (lower ms = faster) so right-side of dial = faster.
const TTS_DIAL_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3];
const FLASH_DIAL_STEPS = [500, 400, 300, 250, 200, 150, 120, 100]; // ms / word
const CHUNK_DIAL_STEPS = [3500, 3000, 2500, 2000, 1500, 1250, 1000]; // ms / chunk

const snapToStep = (v, arr) =>
  arr.reduce((prev, curr) => (Math.abs(curr - v) < Math.abs(prev - v) ? curr : prev), arr[0]);

const Reader = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  // DMCA-style report modal for the current ebook.
  const [reportOpen, setReportOpen] = useState(false);
  const [showQuizButton, setShowQuizButton] = useState(false);
  const [readingMode, setReadingMode] = useState('normal'); // normal, flash, chunk, tts
  // Active chapter idx — lifted up here so Listen / Flash / Chunk can read
  // ONLY the active chapter's text instead of the entire book. Initialised
  // from localStorage so reopening picks up where the reader left off; lives
  // here (not just in ChapterView) so unmounting the Normal tab doesn't drop
  // it. Flat books (no chapters) keep idx=0 and the derived activeText
  // collapses to book.content as before.
  const [chapterIdx, setChapterIdx] = useState(0);
  
  // Flash reading state
  const [flashWords, setFlashWords] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isFlashPlaying, setIsFlashPlaying] = useState(false);
  const [flashSpeed, setFlashSpeed] = useState(300); // ms per word (300 = 200 WPM)
  
  // Chunk reading state
  const [chunks, setChunks] = useState([]);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const [isChunkPlaying, setIsChunkPlaying] = useState(false);
  const [chunkSpeed, setChunkSpeed] = useState(2000); // ms per chunk
  
  // TTS state
  const [isTTSPlaying, setIsTTSPlaying] = useState(false);
  const [ttsSpeed, setTTSSpeed] = useState(1.0);

  // Persisted speed preferences — loaded once, then mirrored into the
  // local mode states above. Subsequent changes are debounced back to the
  // server (see savePref).
  const { prefs: speedPrefs, savePref: saveSpeedPref } = usePlaybackPreferences();
  useEffect(() => {
    if (speedPrefs.flash_wpm) setFlashSpeed(Math.round(60000 / speedPrefs.flash_wpm));
    if (speedPrefs.chunk_ms) setChunkSpeed(speedPrefs.chunk_ms);
    if (speedPrefs.tts_speed) setTTSSpeed(speedPrefs.tts_speed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speedPrefs.flash_wpm, speedPrefs.chunk_ms, speedPrefs.tts_speed]);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [ttsWordIndex, setTtsWordIndex] = useState(0);
  // Becomes true when TTS starts BEFORE Flash/Chunk — TTS then acts as the master clock
  // that drives Flash + Chunk playheads. Cleared only when TTS is explicitly stopped
  // or when the user manually starts an independent Flash/Chunk session.
  const [ttsMaster, setTtsMaster] = useState(false);
  const utteranceRef = useRef(null);
  const audioRef = useRef(null);

  // HD voice state — when true, Listen calls our /api/tts/synthesize backend
  // (OpenAI HD) instead of the browser's robotic speechSynthesis. The MP3 is
  // played through hdAudioRef + persisted in the global AudioPlayerContext
  // mini-player so the user can keep listening while browsing the rest of
  // the app, just like an audiobook.
  const [hdEnabled, setHdEnabled] = useState(() => {
    try { return localStorage.getItem('sl_reader_hd_voice') === '1'; } catch { return false; }
  });
  const [hdVoice, setHdVoice] = useState(() => {
    try { return localStorage.getItem('sl_reader_hd_voice_name') || 'fable'; } catch { return 'fable'; }
  });
  const [hdLoading, setHdLoading] = useState(false);
  // Custom voices from the user's own TTS service (cloudflare-tunneled).
  // Merged into the HD Voice picker so writers/admins who've trained their
  // own voice can pick "The Antagonist 1" or whatever they uploaded.
  const [customVoices, setCustomVoices] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const _BU = process.env.REACT_APP_BACKEND_URL;
    (async () => {
      try {
        const r = await authFetch(`${_BU}/api/tts/voices`);
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        const list = Array.isArray(d.voices) ? d.voices : [];
        // Normalise to {id, label} and dedupe against the OpenAI set so
        // we don't duplicate a voice that already exists upstream.
        const OPENAI_IDS = new Set(['fable', 'nova', 'onyx', 'shimmer', 'echo', 'sage', 'alloy', 'ash', 'coral']);
        const norm = list
          .map((v) => {
            const id = typeof v === 'string' ? v : (v.id || v.name || '');
            const name = typeof v === 'string' ? v : (v.name || v.id || '');
            return id ? { id, label: name } : null;
          })
          .filter(Boolean)
          .filter((v) => !OPENAI_IDS.has(v.id));
        setCustomVoices(norm);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // AI tools
  const [showSummary, setShowSummary] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Chapter-aware text source. When the book has multiple chapters, Listen /
  // Flash / Chunk should read ONLY the active chapter — not the whole book.
  // Falls back to book.content for flat ebooks.
  const validChapters = useMemo(() => {
    const list = Array.isArray(book?.chapters) ? book.chapters : [];
    if (list.length === 1 && (list[0]?.title === 'Full text' || !list[0]?.title)) return [];
    return list.filter((c) => c && c.content && c.content.trim());
  }, [book?.chapters]);
  const hasChapters = validChapters.length > 1;
  const activeText = hasChapters
    ? validChapters[Math.min(chapterIdx, validChapters.length - 1)]?.content || ''
    : (book?.content || '');

  // When the active chapter (or book) changes, stop any running Listen / Flash
  // / Chunk session and reset playheads. Otherwise toggling chapters mid-play
  // would orphan the speech engine on a completely different paragraph.
  useEffect(() => {
    if ('speechSynthesis' in window) {
      try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    }
    setIsTTSPlaying(false);
    setTtsMaster(false);
    setTtsWordIndex(0);
    setCurrentCharIndex(0);
    setIsFlashPlaying(false);
    setCurrentWordIndex(0);
    setIsChunkPlaying(false);
    setCurrentChunkIndex(0);
  }, [activeText]);

  useEffect(() => {
    fetchBook();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  useEffect(() => {
    // Initialize flash reading words from the active chapter (or full book
    // when there are no chapters).
    if (activeText) {
      const words = activeText.split(/\s+/).filter(w => w.length > 0);
      setFlashWords(words);

      // Create chunks (3-5 words per chunk)
      const chunksArray = [];
      for (let i = 0; i < words.length; i += 4) {
        chunksArray.push(words.slice(i, i + 4).join(' '));
      }
      setChunks(chunksArray);
    } else {
      setFlashWords([]);
      setChunks([]);
    }
  }, [activeText]);

  // Flash reading timer — SKIPS running when TTS is the master clock
  useEffect(() => {
    if (ttsMaster) return; // TTS drives the word index directly
    if (isFlashPlaying && currentWordIndex < flashWords.length) {
      const timer = setTimeout(() => {
        setCurrentWordIndex(prev => prev + 1);
      }, flashSpeed);
      return () => clearTimeout(timer);
    } else if (currentWordIndex >= flashWords.length) {
      setIsFlashPlaying(false);
    }
  }, [isFlashPlaying, currentWordIndex, flashSpeed, flashWords.length, ttsMaster]);

  // Chunk reading timer — SKIPS running when TTS is the master clock
  useEffect(() => {
    if (ttsMaster) return; // TTS drives the chunk index directly
    if (isChunkPlaying && currentChunkIndex < chunks.length) {
      const timer = setTimeout(() => {
        setCurrentChunkIndex(prev => prev + 1);
      }, chunkSpeed);
      return () => clearTimeout(timer);
    } else if (currentChunkIndex >= chunks.length) {
      setIsChunkPlaying(false);
    }
  }, [isChunkPlaying, currentChunkIndex, chunkSpeed, chunks.length, ttsMaster]);

  // When the book loads, prime chapterIdx from the per-book localStorage key
  // so a fresh /reader URL resumes on the same chapter (matches ChapterView's
  // saveIdx key exactly).
  useEffect(() => {
    if (!book?.book_id) return;
    try {
      const raw = localStorage.getItem(`sl_reader_chapter_idx::${book.book_id}`);
      const n = parseInt(raw, 10);
      if (Number.isFinite(n) && n >= 0) {
        setChapterIdx(n);
      } else {
        setChapterIdx(0);
      }
    } catch {
      setChapterIdx(0);
    }
  }, [book?.book_id]);

  // When TTS is master, mirror its word progress into Flash + Chunk indexes so
  // switching tabs while Listen is playing shows the currently-spoken word/chunk.
  useEffect(() => {
    if (!ttsMaster) return;
    setCurrentWordIndex(ttsWordIndex);
    setCurrentChunkIndex(Math.floor(ttsWordIndex / 4));
  }, [ttsMaster, ttsWordIndex]);

  const fetchBook = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/books/${bookId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Book not found');
      
      const data = await response.json();
      setBook(data);
      
      // Show quiz button after 3 seconds of reading
      setTimeout(() => setShowQuizButton(true), 3000);
    } catch (error) {
      console.error('Failed to fetch book:', error);
      setLoadError('We couldn’t open this book. It may be unavailable, or your connection may have been interrupted.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQuiz = () => setShowQuiz(true);
  const handleGenerateSummary = () => setShowSummary((prev) => !prev);

  const toggleFlashReading = () => {
    // If Listen is the master clock, Flash/Chunk Play buttons act as TTS play/pause.
    if (ttsMaster) {
      handleTTS();
      return;
    }
    if (isFlashPlaying) {
      setIsFlashPlaying(false);
    } else {
      setIsFlashPlaying(true);
      if (currentWordIndex >= flashWords.length) {
        setCurrentWordIndex(0);
      }
    }
  };

  const toggleChunkReading = () => {
    if (ttsMaster) {
      handleTTS();
      return;
    }
    if (isChunkPlaying) {
      setIsChunkPlaying(false);
    } else {
      setIsChunkPlaying(true);
      if (currentChunkIndex >= chunks.length) {
        setCurrentChunkIndex(0);
      }
    }
  };

  // ── HD TTS (OpenAI fable voice via /api/tts/prepare) ─────────────────────
  // Pipes HD audio through the GLOBAL AudioPlayerContext so playback survives
  // SPA navigation — the listener can keep their HD-narrated chapter playing
  // while they browse the rest of the app, exactly like a cinematic episode.
  // We use /tts/prepare to get a stable cache URL (instead of a session-bound
  // blob URL) because the mini-player needs a reusable string src.
  const audioPlayer = useAudioPlayer();
  const hdAbortRef = useRef(null);

  // The track id we use for HD chapters: combines the book id + chapter idx +
  // voice so swapping any of those produces a new "track" in the mini-player
  // (and the bar flips its title cleanly).
  const hdTrackId = useMemo(() => {
    if (!book?.book_id) return null;
    return `tts::${book.book_id}::${hasChapters ? chapterIdx : 'full'}::${hdVoice}`;
  }, [book?.book_id, hasChapters, chapterIdx, hdVoice]);

  const playHdAudio = async (overrides = {}) => {
    // Allow callers (e.g. auto-advance) to pass an explicit text + chapter
    // index so they don't have to rely on this function's closure being in
    // sync with the latest React render.
    const text = overrides.text || activeText;
    const idxForId = overrides.chapterIdx != null ? overrides.chapterIdx : chapterIdx;
    const titleOverride = overrides.title;
    if (!text || !text.trim()) {
      toast.error('No text to read aloud');
      return;
    }
    if (hdAbortRef.current) {
      try { hdAbortRef.current.abort(); } catch { /* ignore */ }
    }
    const ac = new AbortController();
    hdAbortRef.current = ac;
    setHdLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/tts/prepare`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice: hdVoice, model: 'tts-1-hd' }),
        signal: ac.signal,
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      const { url } = await r.json();
      const fullUrl = `${BACKEND_URL}${url}`;

      try { window.speechSynthesis.cancel(); } catch { /* ignore */ }

      const trackId = `tts::${book?.book_id}::${hasChapters ? idxForId : 'full'}::${hdVoice}`;
      const chapterTitle = titleOverride || (hasChapters
        ? validChapters[idxForId]?.title || `Chapter ${idxForId + 1}`
        : (book?.title || 'Untitled'));
      // If the listener has a saved position for this exact (book, chapter,
      // voice) tuple, pass it via `last_position` so AudioPlayerContext seeks
      // to it once the cached mp3's metadata loads. Resumes a 30-min chapter
      // at minute 12, exactly like cinematic episodes do.
      const resumeSec = savedHdPosition(idxForId, hdVoice);
      audioPlayer.load({
        id: trackId,
        url: fullUrl,
        title: chapterTitle,
        author: book?.author || '',
        type: 'tts',
        cover: book?.cover_image || book?.cover_image_url || '',
        series_title: hasChapters ? (book?.title || '') : '',
        last_position: resumeSec,
      });
      audioPlayer.setPlaybackSpeed(ttsSpeed);
      await audioPlayer.play();
      setIsTTSPlaying(true);
      setTtsMaster(true);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('HD TTS failed:', err);
      toast.error(`HD voice failed: ${err.message || err}`);
      setIsTTSPlaying(false);
    } finally {
      setHdLoading(false);
    }
  };

  const pauseHdAudio = () => {
    audioPlayer.pause();
    setIsTTSPlaying(false);
  };

  const resumeHdAudio = async () => {
    // If the mini-player is still loaded with our HD track, resume it.
    // Otherwise re-prepare from /tts/prepare (cache hit makes this instant).
    if (audioPlayer.track && audioPlayer.track.id === hdTrackId) {
      await audioPlayer.play();
      setIsTTSPlaying(true);
    } else {
      await playHdAudio();
    }
  };

  // Sync playback rate when ttsSpeed slider moves (only when HD path owns
  // the mini-player — otherwise we'd stomp on cinematic episodes a user is
  // listening to in the background).
  useEffect(() => {
    if (audioPlayer.track && audioPlayer.track.id === hdTrackId) {
      audioPlayer.setPlaybackSpeed(ttsSpeed);
    }
  }, [ttsSpeed, audioPlayer, hdTrackId]);

  // Keep our isTTSPlaying flag in sync with the mini-player's actual state
  // — the user can pause/resume from the bar itself, and Reader's UI must
  // reflect that.
  useEffect(() => {
    if (audioPlayer.track && audioPlayer.track.id === hdTrackId) {
      setIsTTSPlaying(audioPlayer.isPlaying);
    }
  }, [audioPlayer.isPlaying, audioPlayer.track, hdTrackId]);

  useEffect(() => {
    return () => {
      if (hdAbortRef.current) { try { hdAbortRef.current.abort(); } catch {} }
      // Don't clear the mini-player on unmount — the whole point of this
      // refactor is that listeners can leave the Reader and keep listening.
    };
  }, []);

  // Persist toggles so users don't have to re-flip on every page load.
  useEffect(() => {
    try { localStorage.setItem('sl_reader_hd_voice', hdEnabled ? '1' : '0'); } catch {}
  }, [hdEnabled]);
  useEffect(() => {
    try { localStorage.setItem('sl_reader_hd_voice_name', hdVoice); } catch {}
  }, [hdVoice]);

  // When the HD voice changes mid-playback, re-prepare with the new voice so
  // the listener hears the change immediately.
  useEffect(() => {
    if (!hdEnabled) return;
    if (audioPlayer.track && audioPlayer.track.id?.startsWith(`tts::${book?.book_id}::`) && audioPlayer.isPlaying) {
      playHdAudio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hdVoice]);

  // ── Per-chapter HD listening progress ────────────────────────────────────
  // The global AudioPlayerContext already persists the LAST played track's
  // position to localStorage so a refresh resumes mid-chapter for the active
  // track. But when the listener jumps to a DIFFERENT chapter and later
  // comes back to chapter 1, they should land where they left off — not at
  // 0:00. We solve this with a per-(book, chapter, voice) position map.
  const HD_POS_KEY = book?.book_id ? `sl_reader_hd_positions::${book.book_id}` : null;

  const readSavedHdPositions = useCallback(() => {
    if (!HD_POS_KEY) return {};
    try {
      const raw = localStorage.getItem(HD_POS_KEY);
      return raw ? (JSON.parse(raw) || {}) : {};
    } catch {
      return {};
    }
  }, [HD_POS_KEY]);

  const savedHdPosition = (idx, voice) => {
    if (!HD_POS_KEY) return 0;
    const map = readSavedHdPositions();
    const k = `${idx}::${voice}`;
    const v = map[k];
    if (typeof v === 'number') return v > 1 ? v : 0;
    if (v && typeof v === 'object') {
      // Don't auto-resume a chapter the listener marked complete — they want
      // a clean re-read.
      if (v.completed) return 0;
      return Number.isFinite(v.pos) && v.pos > 1 ? v.pos : 0;
    }
    return 0;
  };

  // Persist the current HD track's position every 5s while it's playing in
  // our mini-player. We bypass the "is HD currently the global track?" check
  // by reading `audioPlayer.track.id` — that prefix tells us this is OUR
  // book's HD chapter and not a cinematic episode running in the background.
  // Map shape: { "<idx>::<voice>": { pos, duration, completed } }. Old
  // numeric values are still accepted on read for backwards compat.
  useEffect(() => {
    if (!HD_POS_KEY) return undefined;
    const tick = () => {
      const t = audioPlayer.track;
      if (!t || !t.id) return;
      const m = t.id.match(/^tts::([^:]+(?::[^:]+)*)::(\d+|full)::([^:]+)$/);
      if (!m) return;
      const bookIdInId = m[1];
      const idxStr = m[2];
      const voiceInId = m[3];
      if (bookIdInId !== book?.book_id) return;
      const seconds = audioPlayer.currentTime || 0;
      if (seconds < 3) return;
      const map = readSavedHdPositions();
      const key = `${idxStr}::${voiceInId}`;
      const prev = map[key];
      const prevPos = typeof prev === 'number' ? prev : (prev?.pos || 0);
      if (Math.abs(prevPos - seconds) < 2) return;
      map[key] = {
        pos: seconds,
        duration: audioPlayer.duration || (typeof prev === 'object' ? prev.duration : 0) || 0,
        completed: false,
      };
      try { localStorage.setItem(HD_POS_KEY, JSON.stringify(map)); } catch { /* ignore */ }
    };
    const interval = setInterval(tick, 5000);
    const onHide = () => tick();
    window.addEventListener('pagehide', onHide);
    window.addEventListener('beforeunload', onHide);
    return () => {
      clearInterval(interval);
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('beforeunload', onHide);
    };
  }, [HD_POS_KEY, audioPlayer, book?.book_id, readSavedHdPositions]);

  // ── Per-chapter HD progress map for the TOC ──────────────────────────────
  // Derives a {idx: {ratio, completed}} map combining saved positions with
  // the LIVE audio state so the chapter currently playing animates in real
  // time rather than only updating every 5s when we persist to localStorage.
  const hdProgressByIdx = useMemo(() => {
    if (!HD_POS_KEY || !hasChapters) return {};
    let map = {};
    try {
      const raw = localStorage.getItem(HD_POS_KEY);
      map = raw ? (JSON.parse(raw) || {}) : {};
    } catch { map = {}; }
    const out = {};
    const t = audioPlayer.track;
    let liveIdx = -1;
    if (t && t.id) {
      const m = t.id.match(/^tts::([^:]+(?::[^:]+)*)::(\d+|full)::([^:]+)$/);
      if (m && m[1] === book?.book_id && m[3] === hdVoice && m[2] !== 'full') {
        liveIdx = parseInt(m[2], 10);
      }
    }
    for (let i = 0; i < validChapters.length; i++) {
      const k = `${i}::${hdVoice}`;
      const v = map[k];
      let pos = 0, duration = 0, completed = false;
      if (typeof v === 'number') pos = v;
      else if (v && typeof v === 'object') {
        pos = Number(v.pos) || 0;
        duration = Number(v.duration) || 0;
        completed = !!v.completed;
      }
      if (i === liveIdx) {
        const livePos = audioPlayer.currentTime || 0;
        const liveDur = audioPlayer.duration || duration;
        if (livePos > pos) pos = livePos;
        if (liveDur > 0) duration = liveDur;
      }
      if (pos > 0 || completed || duration > 0) {
        const ratio = completed
          ? 1
          : (duration > 0 ? Math.min(1, pos / duration) : 0);
        out[i] = { ratio, completed, isLive: i === liveIdx && audioPlayer.isPlaying };
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    HD_POS_KEY,
    hasChapters,
    validChapters,
    hdVoice,
    book?.book_id,
    audioPlayer.track,
    audioPlayer.currentTime,
    audioPlayer.duration,
    audioPlayer.isPlaying,
  ]);

  // Clear a saved position once the chapter actually ends — if the listener
  // finished it, coming back should start clean (next play is from 0:00).
  // The auto-advance handler handles this when the audio's `ended` event
  // fires, so we don't need a separate listener here.

  // When the current HD chapter ends and there's a next chapter, automatically
  // synth + play it — turning a long PDF into a fully autonomous audiobook
  // session. We register a one-shot end handler with the audio context and
  // re-register it whenever chapterIdx / hdEnabled / hasChapters changes so
  // it always reads the latest state. The handler only fires for OUR HD
  // tracks (id starts with `tts::<book_id>::`) — cinematic episode endings
  // continue to be handled by Player.jsx as before.
  useEffect(() => {
    if (!hdEnabled || !hasChapters) {
      // Don't touch the global onEnded — Player.jsx (cinematic) may have
      // registered its own handler. We only register/clear when this Reader
      // is actively driving HD playback for a multi-chapter book.
      return undefined;
    }
    const handler = () => {
      const t = audioPlayer.track;
      // Only auto-advance when the track that just ended is OUR HD chapter.
      if (!t || !t.id || !t.id.startsWith(`tts::${book?.book_id}::`)) return;
      // Mark the finished chapter as completed (instead of deleting) so the
      // TOC can render a "done" indicator and the per-chapter progress UI
      // stays accurate. Resume logic in `savedHdPosition()` already returns
      // 0 for completed entries — so the next play still starts clean.
      if (HD_POS_KEY) {
        try {
          const map = readSavedHdPositions();
          const key = `${chapterIdx}::${hdVoice}`;
          const prev = map[key];
          const prevDur = (prev && typeof prev === 'object' && prev.duration)
            || audioPlayer.duration || 0;
          map[key] = { pos: prevDur, duration: prevDur, completed: true };
          localStorage.setItem(HD_POS_KEY, JSON.stringify(map));
        } catch { /* ignore */ }
      }
      const next = chapterIdx + 1;
      if (next >= validChapters.length) {
        // Reached the last chapter — leave the bar showing "ended" so the
        // listener sees the natural finish state.
        toast.success('Finished — last chapter complete');
        return;
      }
      // Bumping chapterIdx triggers our other useEffect (activeText reset)
      // and the HD path re-syntheses on the next handleTTS — but the user
      // expects auto-CONTINUE without tapping, so we kick playHdAudio()
      // ourselves. We pass the next chapter's text + idx explicitly because
      // playHdAudio()'s closure won't see the new state until React re-runs
      // this effect.
      setChapterIdx(next);
      const nextChapter = validChapters[next];
      const nextTitle = nextChapter?.title || `Chapter ${next + 1}`;
      toast.success(`Up next: ${nextTitle}`, { duration: 2500 });
      playHdAudio({
        text: nextChapter?.content,
        chapterIdx: next,
        title: nextTitle,
      });
    };
    audioPlayer.setOnEnded(handler);
    return () => audioPlayer.setOnEnded(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hdEnabled, hasChapters, chapterIdx, validChapters.length, book?.book_id, hdVoice, HD_POS_KEY]);

  const handleTTS = async () => {
    // HD voice path — uses our backend OpenAI synthesis instead of the
    // browser's built-in voice.
    if (hdEnabled) {
      // If the mini-player currently holds OUR track and it's playing, pause.
      if (audioPlayer.track && audioPlayer.track.id === hdTrackId && audioPlayer.isPlaying) {
        pauseHdAudio();
      } else if (audioPlayer.track && audioPlayer.track.id === hdTrackId) {
        // Same track loaded but paused — just resume from current position.
        await resumeHdAudio();
      } else {
        // New track (different chapter / voice / book) — synthesize + load.
        setIsFlashPlaying(false);
        setIsChunkPlaying(false);
        await playHdAudio();
      }
      return;
    }

    if (isTTSPlaying) {
      // Pause TTS
      window.speechSynthesis.pause();
      setIsTTSPlaying(false);
    } else {
      // Check if already paused and resume
      if (window.speechSynthesis.paused && utteranceRef.current) {
        window.speechSynthesis.resume();
        setIsTTSPlaying(true);
      } else {
        // Start new TTS from current position — TTS becomes master clock so Flash/Chunk sync to it.
        setTtsMaster(true);
        // Ensure any standalone flash/chunk sessions are paused.
        setIsFlashPlaying(false);
        setIsChunkPlaying(false);
        startTTSFromPosition(currentCharIndex);
      }
    }
  };

  // Stop TTS and release master-clock mode (used by UI "Stop" affordance
  // and by cleanup paths).
  const stopTTS = () => {
    window.speechSynthesis.cancel();
    setIsTTSPlaying(false);
    setTtsMaster(false);
    setTtsWordIndex(0);
    setCurrentCharIndex(0);
  };

  const startTTSFromPosition = (charIndex = 0) => {
    if ('speechSynthesis' in window && activeText) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();

      // Get text from current position within the active chapter / book
      const textToSpeak = activeText.substring(charIndex);
      
      const utterance = new SpeechSynthesisUtterance(textToSpeak);
      utterance.rate = ttsSpeed;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      
      // Try to use a better voice if available
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(v => 
        v.lang.startsWith('en') && (v.name.includes('Google') || v.name.includes('Microsoft'))
      ) || voices[0];
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }

      // Track position using boundary events
      utterance.onboundary = (event) => {
        if (event.name === 'word') {
          const absoluteChar = charIndex + event.charIndex;
          setCurrentCharIndex(absoluteChar);
          // Count words spoken up to this character — drives Flash/Chunk when TTS is master.
          if (activeText) {
            const wordsSpoken = (activeText.substring(0, absoluteChar).match(/\S+/g) || []).length;
            setTtsWordIndex(wordsSpoken);
          }
        }
      };
      
      utterance.onend = () => {
        setIsTTSPlaying(false);
        setCurrentCharIndex(0); // Reset to start
        // Release master-clock mode so independent Flash/Chunk can run again.
        setTtsMaster(false);
      };
      
      utterance.onerror = (e) => {
        console.error('TTS Error:', e);
        setIsTTSPlaying(false);
      };
      
      utteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
      setIsTTSPlaying(true);
    }
  };

  const handleTTSSpeedChange = (value) => {
    const newSpeed = value[0];
    setTTSSpeed(newSpeed);
    saveSpeedPref('tts_speed', newSpeed);
    
    // If currently playing or paused, restart from current position with new speed
    if (isTTSPlaying || window.speechSynthesis.paused) {
      const wasPaused = !isTTSPlaying;
      const currentPos = currentCharIndex;
      
      // Cancel current speech
      window.speechSynthesis.cancel();
      
      // Small delay to ensure cancellation completes
      setTimeout(() => {
        // Start from current position with new speed
        startTTSFromPosition(currentPos);
        
        // If it was paused, pause it again
        if (wasPaused) {
          setTimeout(() => {
            window.speechSynthesis.pause();
            setIsTTSPlaying(false);
          }, 100);
        }
      }, 50);
    }
  };

  if (loading) {
    return (
      <main className="min-h-[70vh] bg-background px-4 py-8 sm:px-6" aria-busy="true" aria-live="polite">
        <div className="mx-auto max-w-4xl animate-pulse motion-reduce:animate-none">
          <div className="h-12 rounded-2xl border border-white/5 bg-card/70" />
          <div className="mt-4 h-[60vh] rounded-2xl border border-white/5 bg-card/70" />
          <p className="sr-only">Loading book</p>
        </div>
      </main>
    );
  }

  if (loadError || !book) {
    return (
      <main className="min-h-[70vh] bg-background px-4 py-10 sm:px-6 grid place-items-center">
        <section className="w-full max-w-lg rounded-2xl border border-white/10 bg-card p-6 sm:p-8 text-center shadow-2xl" role="alert">
          <BookOpen className="mx-auto h-10 w-10 text-violet-300" aria-hidden="true" />
          <h1 className="mt-4 font-display text-3xl font-black text-white">This book couldn’t be opened</h1>
          <p className="mt-3 text-sm leading-6 text-white/65">{loadError || 'No readable book data was returned.'}</p>
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
            <Button type="button" variant="ghost" onClick={() => navigate('/library')}>Back to Library</Button>
            <Button type="button" onClick={fetchBook}>Try again</Button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background text-foreground">
      {/* Internal header — sits above the AppShell TopBar; kept slim so
          the reading area dominates the viewport. */}
      <header className="bg-card/90 backdrop-blur-md border-b border-white/10 z-40 flex-shrink-0">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-3">
          <Button
            onClick={() => navigate('/library')}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10 text-xs sm:text-sm focus-visible:ring-amber-300"
            aria-label="Back to Library"
          >
            <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" aria-hidden="true" />
            <span className="hidden sm:inline">Back to Library</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <div className="flex min-w-0 items-center gap-2">
            <BookCover
              book={book}
              className="h-9 w-6 shrink-0 rounded border border-white/10 sm:h-10 sm:w-7"
              priority
            />
            <p className="min-w-0 truncate text-sm font-semibold text-white sm:text-base" title={book?.title}>{book?.title}</p>
          </div>
        </div>
      </header>

      {/* Reader Content — fills remaining viewport. Inner sections own their
          own scrolling so the page itself never scrolls. */}
      <main className="flex-1 min-h-0 max-w-6xl w-full mx-auto px-2 sm:px-6 py-2 sm:py-4">
        <div className="bg-card/75 rounded-2xl p-3 sm:p-5 shadow-[0_24px_80px_rgba(0,0,0,0.32)] border border-white/10 h-full flex flex-col">
          {/* Book Info — kept compact so the reading area dominates the
              viewport. Description + summary panel collapse to overflow. */}
          <div className="mb-3 pb-3 border-b border-white/10 flex-shrink-0">
            <h1 className="font-display text-xl sm:text-3xl font-black tracking-tight text-white mb-1 line-clamp-2">{book?.title}</h1>
            <div className="flex items-center justify-between flex-wrap gap-2">
              {book?.user_id ? (
                <button
                  type="button"
                  onClick={() => navigate(`/writer/${book.user_id}`)}
                  className="text-sm sm:text-base text-gray-400 hover:text-indigo-300 truncate underline-offset-2 hover:underline focus:outline-none focus:underline text-left"
                  data-testid="reader-author-link"
                >
                  {book?.author}
                </button>
              ) : (
                <p className="text-sm sm:text-base text-gray-400 truncate">{book?.author}</p>
              )}
              <DonateButton
                recipientId={book?.user_id}
                recipientName={book?.author}
              />
            </div>

            {/* Admin-pre-generated TTS audio (`audio_url` on the content
                record). Renders inline so readers can hit play without
                going through the dynamic TTS reader-mode. */}
            {book?.audio_url && (
              <div className="mt-2.5" data-testid="reader-prebuilt-audio">
                <p className="text-[10px] uppercase tracking-widest font-bold text-indigo-300 mb-1">
                  Listen — audio version
                </p>
                <audio
                  controls
                  src={book.audio_url}
                  preload="none"
                  className="w-full"
                  data-testid="reader-prebuilt-audio-player"
                >
                  Your browser doesn’t support inline audio playback.
                </audio>
              </div>
            )}

            {/* AI tools bar — kept inline next to author so it doesn't add
                another vertical row on small screens. */}
            <div className="flex flex-wrap gap-2 mt-2">
              <Button
                onClick={handleGenerateSummary}
                variant="outline"
                size="sm"
                className="border-indigo-600 text-indigo-300 hover:bg-indigo-900/30 h-7 text-xs"
                data-testid="reader-summary-btn"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                {showSummary ? 'Hide Summary' : 'AI Summary'}
              </Button>
              <Button
                onClick={handleGenerateQuiz}
                variant="outline"
                size="sm"
                className="border-green-600 text-green-300 hover:bg-green-900/30 h-7 text-xs"
                data-testid="reader-quiz-btn"
              >
                <Award className="w-3.5 h-3.5 mr-1.5" />
                Take Quiz
              </Button>
            </div>

            {/* Summary panel (inline). Sits inside the scrollable region of
                each tab, so users on small screens still keep tabs visible
                while the panel overlays the prose. */}
            {showSummary && (
              <div className="mt-3">
                <SummaryPanel
                  contentId={bookId}
                  contentTitle={book?.title}
                  onClose={() => setShowSummary(false)}
                />
              </div>
            )}
          </div>

          {/* Reading Mode Selector — flex-1 + min-h-0 lets the active
              TabsContent claim the rest of the viewport with its own
              internal scrolling. */}
          <Tabs
            value={readingMode}
            onValueChange={setReadingMode}
            className="flex-1 min-h-0 flex flex-col"
          >
            <TabsList className="grid h-auto w-full grid-cols-4 gap-1 rounded-xl border border-white/10 bg-background/70 p-1 flex-shrink-0">
              <TabsTrigger value="normal" className="min-h-10 rounded-lg px-2 text-xs sm:text-sm data-[state=active]:bg-blue-600 focus-visible:ring-amber-300">
                <Book className="hidden w-4 h-4 mr-2 sm:block" aria-hidden="true" />
                Normal
              </TabsTrigger>
              <TabsTrigger value="flash" className="min-h-10 rounded-lg px-2 text-xs sm:text-sm data-[state=active]:bg-purple-600 focus-visible:ring-amber-300">
                <Zap className="hidden w-4 h-4 mr-2 sm:block" aria-hidden="true" />
                Flash
              </TabsTrigger>
              <TabsTrigger value="chunk" className="min-h-10 rounded-lg px-2 text-xs sm:text-sm data-[state=active]:bg-green-600 focus-visible:ring-amber-300">
                <Layers className="hidden w-4 h-4 mr-2 sm:block" aria-hidden="true" />
                Chunk
              </TabsTrigger>
              <TabsTrigger value="tts" className="min-h-10 rounded-lg px-2 text-xs sm:text-sm data-[state=active]:bg-orange-600 focus-visible:ring-amber-300">
                <Volume2 className="hidden w-4 h-4 mr-2 sm:block" aria-hidden="true" />
                Listen
              </TabsTrigger>
            </TabsList>

            {/* Normal Reading Mode */}
            <TabsContent value="normal" className="flex-1 min-h-0 mt-3 overflow-y-auto">
              <div className="prose prose-invert prose-lg max-w-none">
                {book?.content?.startsWith('http') ? (
                  <div className="space-y-4">
                    <p className="text-gray-300">
                      This book is hosted on Project Gutenberg.
                    </p>
                    <a
                      href={book.content}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block"
                    >
                      <Button className="bg-indigo-600 hover:bg-indigo-700">
                        <BookOpen className="w-4 h-4 mr-2" />
                        Read on Project Gutenberg
                      </Button>
                    </a>
                  </div>
                ) : !book?.content || !book.content.trim() ? (
                  // No extracted text — surface a download link instead of
                  // showing a blank page. Happens for scanned PDFs, .mobi
                  // files, and admin uploads that pre-date the text extractor.
                  // For images (PNG/JPG/JPEG) we render the file inline so
                  // picture-book/scanned-page uploads work as a native reader.
                  (() => {
                    const fmt = (book?.file_format || '').toLowerCase();
                    const isImage = ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(fmt);
                    const fileHref = book?.url
                      ? book.url
                      : (book?.file_url ? `${BACKEND_URL}/api${book.file_url}` : null);
                    if (isImage && fileHref) {
                      return (
                        <div className="space-y-3 text-center py-4" data-testid="reader-image-ebook-view">
                          <img
                            src={fileHref}
                            alt={book?.title || 'Ebook page'}
                            className="max-w-full max-h-[80vh] mx-auto rounded-lg shadow-lg shadow-black/40"
                          />
                          <a
                            href={fileHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block text-xs text-indigo-300 hover:text-indigo-200 underline"
                            data-testid="reader-image-download-link"
                          >
                            Open full size in a new tab
                          </a>
                        </div>
                      );
                    }
                    return (
                      <div className="space-y-4 text-center py-10" data-testid="reader-no-text-fallback">
                        <BookOpen className="w-12 h-12 mx-auto text-gray-500" />
                        <p className="text-gray-300 text-base font-semibold">
                          We couldn&apos;t render this {(book?.file_format || 'file').toUpperCase()} inline.
                        </p>
                        <p className="text-gray-500 text-sm max-w-md mx-auto">
                          You can still open the original file. Admins can run
                          &ldquo;Re-extract ebook text&rdquo; from the Admin Panel
                          to back-fill text for old uploads.
                        </p>
                        {fileHref && (
                          <a
                            href={fileHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block"
                            data-testid="reader-download-fallback-link"
                          >
                            <Button className="bg-indigo-600 hover:bg-indigo-700">
                              <BookOpen className="w-4 h-4 mr-2" />
                              Open original file
                            </Button>
                          </a>
                        )}
                      </div>
                    );
                  })()
                ) : (
                  <ChapterView
                    book={book}
                    chapterIdx={chapterIdx}
                    onChapterChange={setChapterIdx}
                    hdProgressByIdx={hdProgressByIdx}
                  />
                )}
              </div>
            </TabsContent>

            {/* Flash Reading Mode */}
            <TabsContent value="flash" className="flex-1 min-h-0 mt-3 overflow-y-auto">
              {hasChapters && (
                <ChapterPickerInline
                  chapters={validChapters}
                  idx={chapterIdx}
                  onChange={setChapterIdx}
                  accent="purple"
                />
              )}
              <Card className="bg-slate-900/50 border-slate-600 mb-4">
                <CardContent className="p-3 sm:p-5">
                  {ttsMaster && (
                    <div
                      className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-orange-900/20 border border-orange-700/50 px-3 py-2"
                      data-testid="reader-flash-synced-indicator"
                    >
                      <div className="flex items-center gap-2 text-orange-200 text-xs font-semibold">
                        <Volume2 className="w-3.5 h-3.5" />
                        Synced with Listen — flashing spoken word
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-orange-200 hover:text-white hover:bg-orange-700/40 h-7 text-xs"
                        onClick={stopTTS}
                        data-testid="reader-flash-unlink-btn"
                      >
                        Stop Listen
                      </Button>
                    </div>
                  )}
                  <div className="flex items-center gap-4 mb-3">
                    <Button
                      onClick={toggleFlashReading}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="reader-flash-toggle"
                    >
                      {(ttsMaster ? isTTSPlaying : isFlashPlaying) ? (
                        <><Pause className="w-4 h-4 mr-2" /> Pause</>
                      ) : (
                        <><Play className="w-4 h-4 mr-2" /> Start</>
                      )}
                    </Button>
                    <span className="text-gray-400 text-sm">
                      {currentWordIndex} / {flashWords.length} words
                    </span>
                  </div>

                  <div className="mb-3 flex justify-center">
                    {ttsMaster ? (
                      <SpeedDial
                        value={snapToStep(ttsSpeed, TTS_DIAL_STEPS)}
                        onChange={(v) => handleTTSSpeedChange([v])}
                        steps={TTS_DIAL_STEPS}
                        accent="amber"
                        live
                        size="md"
                        title="TTS speed (driving Flash)"
                        subLabel={ttsSpeed < 1 ? 'Slower' : ttsSpeed > 1 ? 'Faster' : 'Normal'}
                        testIdPrefix="flash-tts-dial"
                      />
                    ) : (
                      <SpeedDial
                        value={snapToStep(flashSpeed, FLASH_DIAL_STEPS)}
                        onChange={(v) => {
                          setFlashSpeed(v);
                          saveSpeedPref('flash_wpm', Math.round(60000 / v));
                        }}
                        steps={FLASH_DIAL_STEPS}
                        accent="fuchsia"
                        live
                        size="md"
                        title="Flash speed"
                        formatValue={(v) => `${Math.round(60000 / v)}`}
                        formatTick={(v) => `${Math.round(60000 / v)}`}
                        subLabel="Words / min"
                        testIdPrefix="flash-dial"
                      />
                    )}
                  </div>
                  
                  <div className="min-h-[140px] sm:min-h-[200px] flex items-center justify-center bg-black rounded-lg p-6 sm:p-10">
                    <p className="text-4xl sm:text-5xl font-bold text-white text-center">
                      {flashWords[currentWordIndex] || 'Ready'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Chunk Reading Mode */}
            {/* Chunk Reading Mode */}
            <TabsContent value="chunk" className="flex-1 min-h-0 mt-3 overflow-y-auto">
              {hasChapters && (
                <ChapterPickerInline
                  chapters={validChapters}
                  idx={chapterIdx}
                  onChange={setChapterIdx}
                  accent="green"
                />
              )}
              <Card className="bg-slate-900/50 border-slate-600 mb-4">
                <CardContent className="p-3 sm:p-5">
                  {ttsMaster && (
                    <div
                      className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-orange-900/20 border border-orange-700/50 px-3 py-2"
                      data-testid="reader-chunk-synced-indicator"
                    >
                      <div className="flex items-center gap-2 text-orange-200 text-xs font-semibold">
                        <Volume2 className="w-3.5 h-3.5" />
                        Synced with Listen — chunk advances with speech
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-orange-200 hover:text-white hover:bg-orange-700/40 h-7 text-xs"
                        onClick={stopTTS}
                        data-testid="reader-chunk-unlink-btn"
                      >
                        Stop Listen
                      </Button>
                    </div>
                  )}
                  <div className="flex items-center gap-4 mb-3">
                    <Button
                      onClick={toggleChunkReading}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="reader-chunk-toggle"
                    >
                      {(ttsMaster ? isTTSPlaying : isChunkPlaying) ? (
                        <><Pause className="w-4 h-4 mr-2" /> Pause</>
                      ) : (
                        <><Play className="w-4 h-4 mr-2" /> Start</>
                      )}
                    </Button>
                    <span className="text-gray-400 text-sm">
                      {currentChunkIndex} / {chunks.length} chunks
                    </span>
                  </div>

                  <div className="mb-3 flex justify-center">
                    {ttsMaster ? (
                      <SpeedDial
                        value={snapToStep(ttsSpeed, TTS_DIAL_STEPS)}
                        onChange={(v) => handleTTSSpeedChange([v])}
                        steps={TTS_DIAL_STEPS}
                        accent="amber"
                        live
                        size="md"
                        title="TTS speed (driving Chunk)"
                        subLabel={ttsSpeed < 1 ? 'Slower' : ttsSpeed > 1 ? 'Faster' : 'Normal'}
                        testIdPrefix="chunk-tts-dial"
                      />
                    ) : (
                      <SpeedDial
                        value={snapToStep(chunkSpeed, CHUNK_DIAL_STEPS)}
                        onChange={(v) => {
                          setChunkSpeed(v);
                          saveSpeedPref('chunk_ms', v);
                        }}
                        steps={CHUNK_DIAL_STEPS}
                        accent="emerald"
                        live
                        size="md"
                        title="Chunk speed"
                        formatValue={(v) => `${(v / 1000).toFixed(1)}s`}
                        formatTick={(v) => `${(v / 1000).toFixed(1)}s`}
                        subLabel="Per chunk"
                        testIdPrefix="chunk-dial"
                      />
                    )}
                  </div>
                  
                  <div className="min-h-[140px] sm:min-h-[200px] flex items-center justify-center bg-black rounded-lg p-4 sm:p-8">
                    <p className="text-2xl sm:text-3xl font-semibold text-white text-center leading-relaxed">
                      {chunks[currentChunkIndex] || 'Ready to start chunk reading'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* TTS Mode */}
            <TabsContent value="tts" className="flex-1 min-h-0 mt-3 overflow-y-auto">
              {hasChapters && (
                <ChapterPickerInline
                  chapters={validChapters}
                  idx={chapterIdx}
                  onChange={setChapterIdx}
                  accent="orange"
                />
              )}
              <Card className="bg-slate-900/50 border-slate-600">
                <CardContent className="p-3 sm:p-5">
                  {/* HD Voice toggle — flips between browser TTS and OpenAI HD */}
                  <div
                    className="mb-5 rounded-xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-rose-500/10 p-4"
                    data-testid="reader-hd-voice-toggle-card"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-bold">
                            ✨ HD Voice
                          </span>
                          {hdEnabled && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/30 border border-amber-400/50 text-amber-100 font-semibold">
                              ON
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-white font-semibold mt-1">
                          {hdEnabled ? `Narrating with "${hdVoice}"` : 'Switch from robotic to natural narration'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {hdEnabled
                            ? 'Audio is generated server-side and cached per chapter — same chapter replays are instant.'
                            : 'Uses your browser\'s built-in voice. Tap to upgrade to studio-quality OpenAI HD.'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setHdEnabled((v) => !v)}
                        role="switch"
                        aria-checked={hdEnabled}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-amber-400 ${
                          hdEnabled ? 'bg-amber-500' : 'bg-slate-700'
                        }`}
                        data-testid="reader-hd-voice-switch"
                      >
                        <span
                          className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                            hdEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {hdEnabled && (
                      <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="reader-hd-voice-picker">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-bold">
                          Voice
                        </span>
                        {[
                          ...customVoices.map((v) => ({ id: v.id, label: v.label, custom: true })),
                          { id: 'fable', label: 'Fable · expressive' },
                          { id: 'nova', label: 'Nova · upbeat' },
                          { id: 'onyx', label: 'Onyx · deep' },
                          { id: 'shimmer', label: 'Shimmer · bright' },
                          { id: 'echo', label: 'Echo · calm' },
                          { id: 'sage', label: 'Sage · measured' },
                        ].map((v) => (
                          <div
                            key={v.id}
                            className={`inline-flex items-center gap-1.5 rounded-lg border transition-colors ${
                              hdVoice === v.id
                                ? 'bg-amber-500/30 border-amber-400/50'
                                : 'bg-slate-800/60 border-slate-700 hover:bg-slate-700/60'
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => setHdVoice(v.id)}
                              className={`text-[11px] pl-2.5 pr-1 py-1 rounded-l-lg inline-flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 ${
                                hdVoice === v.id
                                  ? 'text-amber-100 font-semibold'
                                  : 'text-gray-300'
                              }`}
                              data-testid={`reader-hd-voice-${v.id}`}
                            >
                              {v.custom && (
                                <span
                                  className="text-[8px] uppercase tracking-widest font-black px-1 py-px rounded bg-fuchsia-500/30 text-fuchsia-100 border border-fuchsia-400/40"
                                  title="Custom voice from your TTS server"
                                >
                                  Yours
                                </span>
                              )}
                              <span className="truncate max-w-[160px]">{v.label}</span>
                            </button>
                            {/* Custom voices only — OpenAI voices aren't
                                routed through /api/tts/generate so a preview
                                via that endpoint would 400 on "unknown voice". */}
                            {v.custom && <VoicePreviewButton voice={v.id} size="xs" className="mr-1" />}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-4 mb-6 flex-wrap">
                    <Button
                      onClick={handleTTS}
                      className="bg-orange-600 hover:bg-orange-700"
                      size="lg"
                      disabled={hdLoading}
                      data-testid="reader-tts-toggle"
                    >
                      {hdLoading ? (
                        <><Volume2 className="w-5 h-5 mr-2 animate-pulse" /> Generating…</>
                      ) : isTTSPlaying ? (
                        <><Pause className="w-5 h-5 mr-2" /> Pause</>
                      ) : (
                        <><Play className="w-5 h-5 mr-2" /> Listen</>
                      )}
                    </Button>
                    <span className="text-gray-400 text-sm">
                      Text-to-Speech Player
                    </span>
                    {ttsMaster && (
                      <span
                        className="px-2 py-0.5 rounded-full bg-orange-500/20 border border-orange-400/40 text-orange-200 text-[10px] font-semibold uppercase tracking-wider"
                        data-testid="reader-tts-master-badge"
                      >
                        Driving Flash + Chunk
                      </span>
                    )}
                  </div>

                  <div className="mb-6 flex justify-center">
                    <SpeedDial
                      value={snapToStep(ttsSpeed, TTS_DIAL_STEPS)}
                      onChange={(v) => handleTTSSpeedChange([v])}
                      steps={TTS_DIAL_STEPS}
                      accent="amber"
                      live
                      size="md"
                      title="TTS speed"
                      subLabel={ttsSpeed < 1 ? 'Slower' : ttsSpeed > 1 ? 'Faster' : 'Normal'}
                      testIdPrefix="tts-dial"
                    />
                  </div>
                  
                  <div className="bg-gradient-to-br from-orange-900/20 to-purple-900/20 rounded-lg p-8 text-center">
                    <Volume2 className="w-16 h-16 text-orange-400 mx-auto mb-4" />
                    <p className="text-gray-300 text-lg mb-2">Audio Narration</p>
                    <p className="text-gray-500 text-sm">
                      Listen to the book being read aloud. Adjust speed to your preference.
                    </p>
                  </div>

                  {/* Show excerpt of text being read — chapter-aware */}
                  <div className="mt-6 p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-gray-400 text-sm mb-2">
                      Currently reading
                      {hasChapters && validChapters[chapterIdx] && (
                        <span className="text-indigo-300 font-semibold">
                          {' '}· {validChapters[chapterIdx].title}
                        </span>
                      )}
                      :
                    </p>
                    <p className="text-gray-300 leading-relaxed line-clamp-4">
                      {activeText.substring(0, 300)}...
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Quiz Button */}
        {showQuizButton && (
          <Card className="mt-6 bg-gradient-to-r from-green-600 to-teal-600 border-0">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Brain className="w-12 h-12 text-white" />
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">Test Your Comprehension</h3>
                  <p className="text-white/80 text-sm">Take an AI-generated quiz on this book</p>
                </div>
              </div>
              <Button 
                onClick={handleGenerateQuiz}
                className="bg-white text-green-600 hover:bg-gray-100 font-semibold px-6"
              >
                <Award className="w-4 h-4 mr-2" />
                Take Quiz
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Comments + rating — surfaced after the reading area on every
          ebook reader page. Feb 26 2026 user spec. */}
      {bookId && (
        <div className="max-w-4xl mx-auto px-4 pb-8" data-testid="reader-comments">
          <ItemComments refId={bookId} refType="book" />
          <div className="mt-4 text-right">
            <button
              type="button"
              onClick={() => setReportOpen(true)}
              className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-rose-300 transition"
              data-testid="reader-report-btn"
            >
              <Flag className="w-3 h-3" /> Report this content
            </button>
          </div>
        </div>
      )}

      <ReportContentModal
        refId={bookId}
        refTitle={book?.title}
        open={reportOpen}
        onClose={() => setReportOpen(false)}
      />

      {showQuiz && (
        <QuizModal
          contentId={bookId}
          contentTitle={book?.title}
          onClose={() => setShowQuiz(false)}
        />
      )}
    </div>
  );
};


export default Reader;
